import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Bell, Loader2, ChevronsUpDown, Check } from "lucide-react";
import { DateTime } from "luxon";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "../ui/command";
import { createCheckpointReminder } from "../../api/checkpointReminders";
import { getUserPrescriptions, Prescription } from "../../api/prescriptions";
import useRetryFetch from "../../hooks/useRetryFetch";
import useAuth from "../../hooks/useAuth";

interface CreateReminderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReminderCreated: () => void;
}

export const CreateReminderModal: React.FC<CreateReminderModalProps> = ({
  open,
  onOpenChange,
  onReminderCreated,
}) => {
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();
  const { user } = useAuth();

  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [isLoadingPrescriptions, setIsLoadingPrescriptions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState<string>("");
  const [reminderDate, setReminderDate] = useState("");
  const [reminderTime, setReminderTime] = useState("");
  const [activityPickerOpen, setActivityPickerOpen] = useState(false);

  const [activitySearch, setActivitySearch] = useState("");

  const filteredPrescriptions = useMemo(() => {
    const term = activitySearch.trim().toLowerCase();
    if (!term) return prescriptions;

    return prescriptions.filter((p) =>
      (p.name || "").toLowerCase().includes(term)
    );
  }, [prescriptions, activitySearch]);

  // Fetch user prescriptions when modal opens
  useEffect(() => {
    const fetchPrescriptions = async () => {
      if (!user?.email) return;

      setIsLoadingPrescriptions(true);
      try {
        // Fetch all prescriptions (limit=1000 to ensure we get everything)
        const response = await getUserPrescriptions(retryFetch, user.email, 1000);
        // The response has data array containing prescriptions
        const prescriptionsList = response.data || [];
        // Filter to only show non-archived prescriptions
        const activePrescriptions = prescriptionsList.filter(
          (p: Prescription) => !p.archived
        );
        setPrescriptions(activePrescriptions);
      } catch (error) {
        console.error("Failed to fetch prescriptions:", error);
        toast.error(t("failed_to_load_activities"));
      } finally {
        setIsLoadingPrescriptions(false);
      }
    };

    if (open && user?.email) {
      fetchPrescriptions();
      // Set default date/time to tomorrow at 9 AM
      const tomorrow = DateTime.now().plus({ days: 1 }).set({ hour: 9, minute: 0 });
      setReminderDate(tomorrow.toFormat("yyyy-MM-dd"));
      setReminderTime(tomorrow.toFormat("HH:mm"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedPrescriptionId) {
      toast.error(t("please_select_activity"));
      return;
    }

    if (!reminderDate || !reminderTime) {
      toast.error(t("please_select_date_time"));
      return;
    }

    // Combine date and time into ISO string
    const dateTime = DateTime.fromFormat(
      `${reminderDate} ${reminderTime}`,
      "yyyy-MM-dd HH:mm"
    );

    if (dateTime < DateTime.now()) {
      toast.error(t("reminder_must_be_future"));
      return;
    }

    setIsSubmitting(true);
    try {
      const selectedPrescription = prescriptions.find((p) => p._id === selectedPrescriptionId);

      // For "start_new_engagement", we only send prescriptionId (no processId)
      // Backend will create the reminder with prescription reference
      // Process will be created automatically when user clicks the email link
      await createCheckpointReminder(retryFetch, {
        prescriptionId: selectedPrescriptionId,
        scheduledDateTime: dateTime.toISO() || "",
        activityTitle: selectedPrescription?.name || t("untitled_activity"),
        reminderAction: "start_new_engagement",
      });

      toast.success(t("reminder_created_success"));
      onReminderCreated();
      onOpenChange(false);

      // Reset form
      setSelectedPrescriptionId("");
      setReminderDate("");
      setReminderTime("");
    } catch (error) {
      console.error("Failed to create reminder:", error);
      toast.error(t("reminder_create_failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            {t("create_reminder")}
          </DialogTitle>
          <DialogDescription>
            {t("create_reminder_description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Activity/Prescription Selector */}
          <div className="space-y-2">
            <Label htmlFor="prescription">{t("select_activity")}</Label>
            {isLoadingPrescriptions ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : prescriptions.length === 0 ? (
              <div className="py-4 text-sm text-center border rounded-md text-muted-foreground bg-muted/30">
                {t("no_activities_available")}
              </div>
            ) : (
              <Popover
                open={activityPickerOpen}
                onOpenChange={setActivityPickerOpen}
              >
                <PopoverTrigger asChild>
                  <div className="relative">
                    <Input
                      id="prescription"
                      role="combobox"
                      aria-expanded={activityPickerOpen}
                      placeholder={t("select_activity_placeholder")}
                      value={activitySearch || (selectedPrescriptionId
                        ? prescriptions.find((p) => p._id === selectedPrescriptionId)?.name ||
                          t("untitled_activity")
                        : "")}
                      onChange={(e) => {
                        setActivitySearch(e.target.value);
                        if (!activityPickerOpen) {
                          setActivityPickerOpen(true);
                        }
                      }}
                      dir={i18n.dir()}
                      className="pr-8"
                    />
                    <ChevronsUpDown className="pointer-events-none absolute inset-y-0 right-2 my-auto h-4 w-4 text-muted-foreground" />
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                  <Command dir={i18n.dir()}>
                    <CommandList>
                      <CommandEmpty>
                        {t("no_matching_activities")}
                      </CommandEmpty>
                      <CommandGroup>
                        {filteredPrescriptions.map((prescription) => {
                          const label =
                            prescription.name || t("untitled_activity");
                          const isSelected =
                            prescription._id === selectedPrescriptionId;

                          return (
                            <CommandItem
                              key={prescription._id}
                              value={label}
                              onSelect={() => {
                                setSelectedPrescriptionId(prescription._id);
                                    setActivitySearch(label);
                                setActivityPickerOpen(false);
                              }}
                            >
                              <span
                                className="block max-w-full whitespace-normal break-words text-start flex-1"
                                dir={i18n.dir()}
                              >
                                {label}
                              </span>
                              {isSelected && (
                                <Check className="ml-2 h-4 w-4 shrink-0" />
                              )}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>

          {/* Date & Time */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("schedule_reminder")}</Label>
            <div className="grid grid-cols-2 gap-3">
              <Input
                id="date"
                type="date"
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
                required
              />
              <Input
                id="time"
                type="time"
                value={reminderTime}
                onChange={(e) => setReminderTime(e.target.value)}
                required
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || isLoadingPrescriptions || prescriptions.length === 0}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("creating")}
                </>
              ) : (
                t("create_reminder")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
